import React, { useEffect } from 'react';
import { FiltersMap } from '@wix/bex-core';
import { CardsGridPresetProps } from './CardsGrid.types';
import { VirtualGridRepeater } from '../VirtualGridRepeater/VirtualGridRepeater';
import { CardsGridState } from '../../state';
import { DragAndDrop } from '../DragAndDrop';
import { useGridSyncProps } from './useGridSyncProps';
import { useRenderItem } from './useRenderItem';
import { getToolbarCollectionFeaturesInitializers } from '../../state/getToolbarCollectionFeaturesInitializers';
import { CardsGridContent } from './CardsGridContent';
import { ToolbarCollectionContentAutoSizer } from '../ToolbarCollectionContentAutoSizer';
import { BulkActionToolbarRenderProp } from '../BulkAction';
import { CardGridItemBaseProps } from './CardGridItemBaseProps';
import { GridPlaceholderStates } from './GridPlaceholderStates';
import { CollectionCard } from '../CollectionCard';
import {
  ToolbarCollection,
  ToolbarCollectionBaseProps,
} from '../ToolbarCollection';
import { observer } from 'mobx-react-lite';

interface CardsGridVirtualBaseProps<T, F extends FiltersMap>
  extends Omit<ToolbarCollectionBaseProps<T, F>, 'sticky'>,
    CardGridItemBaseProps<T, F> {
  dataHook?: string;
  /**
   * Drag and drop implementation: `import { CardsGridDragAndDrop } from '@wix/cairo';`
   * > In order to make the drag and drop also re-order the collection after drop, also add [OptimisticActions](/pages/cairo/?path=/story/components-collection-features--optimistic-actions).
   * @external
   */
  dragAndDrop?: DragAndDrop;

  /**
   * A grid state instance created using [useGrid](/pages/cairo/?path=/story/common-hooks--usegrid).
   * @external
   */
  state: CardsGridState<T, F>;

  /**
   * Render a custom toolbar when more than 1 item is selected.<br>
   * The function accepts the following parameters
   *   * `selectedValues`: The items that were checked via the selection column
   *   * `uncheckedValues`: The items that were unchecked via the selection column
   *   * `isSelectAll`: Indicates whether the "Select All" checkbox is checked
   *   * `openModal`: Opens a modal that contains the component from `bulkActionModal` render prop
   *   * `clearSelection`: Clears all existing selection, call this after the action on the selected items was done
   *   * `total`: Total items in the server (taken from [`fetchData`](/pages/cairo/?path=/story/common-hooks--usecollection) return value).
   *   * `openConfirmModal`: Opens a `<MessageModalLayout/>` to confirm an action about to be applied on the selected items.
   *   * `query`: ComputedQuery<F> - Representation of the current paging, sorting & filtering applied. Usually for using together with bulk action such as `updateAll` and `deleteAll`
   *
   * > When passed, enables `showSelection` by default.
   * @external
   */
  bulkActionToolbar?: BulkActionToolbarRenderProp<T, F>;
}

export type CardsGridVirtualBasePropsWithoutPreset<
  T,
  F extends FiltersMap,
> = Omit<CardsGridVirtualBaseProps<T, F>, 'preset' | 'renderItem'>;

export type CardsGridVirtualProps<
  T,
  F extends FiltersMap,
> = CardsGridPresetProps<T, CardsGridVirtualBasePropsWithoutPreset<T, F>>;

function _CardsGridVirtual<T, F extends FiltersMap = FiltersMap>(
  props: CardsGridVirtualProps<T, F>,
) {
  const {
    state,
    dragAndDrop,
    dragAndDropSubmit,
    dragAndDropCancel,
    renderError,
    renderEmptyState,
    topNotification,
    cardProps,
    dataHook,
    exportModal,
  } = props;

  const { toolbar } = state;

  useGridSyncProps({
    ...props,
    state: state.grid,
  });

  useEffect(
    () =>
      state.init({
        featuresInitializers: getToolbarCollectionFeaturesInitializers(props),
      }),
    [],
  );

  const renderItem = useRenderItem({
    ...props,
    state: state.grid,
  });

  let child = (
    <VirtualGridRepeater renderItem={renderItem} gridState={state.grid} />
  );

  if (dragAndDrop) {
    const { DraggableCardOverlay, SortableContext } = dragAndDrop;

    child = (
      <SortableContext
        state={state.grid}
        onSubmit={dragAndDropSubmit}
        onCancel={dragAndDropCancel}
      >
        {child}
        <DraggableCardOverlay state={state.grid} renderItem={renderItem} />
      </SortableContext>
    );
  }

  return (
    <CollectionCard
      renderError={false}
      state={toolbar}
      dataHook={dataHook}
      {...cardProps}
    >
      <ToolbarCollection {...props} sticky state={toolbar}>
        <GridPlaceholderStates
          state={state.grid}
          renderError={renderError}
          renderEmptyState={renderEmptyState}
          topNotification={topNotification}
        >
          <ToolbarCollectionContentAutoSizer>
            <CardsGridContent state={state}>{child}</CardsGridContent>
          </ToolbarCollectionContentAutoSizer>
        </GridPlaceholderStates>
      </ToolbarCollection>
      {exportModal ?? toolbar.featuredComponents.exportModal.value}
    </CollectionCard>
  );
}

export const CardsGridVirtual = observer(_CardsGridVirtual);
